<template>
	<header>
		<div class="header" :class="{ 'not-home': currentPage !== '#home' || isErrorPage }">
			<div class="header-logo" @click="handlePageRouterClick(pageRouterList[0])">
				<ClientOnly>
					<img :src="currentPage !== '#home' || isErrorPage ? logoBlack : logoWhite" alt="logo" />
				</ClientOnly>
			</div>
			<div class="page-router">
				<div
					class="page-router-item"
					v-for="item in pageRouterList"
					:key="item.id"
					:class="{ current: currentPage === item.path }"
					@click="handlePageRouterClick(item)"
				>
					<template v-if="item.isShowHeader">
						<span class="page-router-item-ja">{{ $t(`header.${item.nameKey}` as string) }}</span>
					</template>
				</div>
			</div>
			<div class="header-right">
				<div class="download-btn hidden-pc" v-if="currentPage !== '#home'" @click="handleDownloadApp">
					<img src="@/assets/imgs/icon-mobile-white.png" alt="pc" />
					<span>{{ $t("header.download") }}</span>
				</div>
				<div class="header-right-icon-box" v-if="currentPage == '#home'">
					<ClientOnly>
						<div class="lang-select-box-container">
							<a class="icon-box" href="#" target="_blank" @click.prevent="handleShowLangSelect">
								<img src="@/assets/imgs/lang-select.webp" alt="lang-select" />
							</a>
							<div class="lang-select-box" v-if="isShowLangSelect">
								<div
									class="lang-select-box-item"
									v-for="item in langSelectList"
									:key="item.id"
									:class="{ active: currentLang === item.lang }"
									@click="handleLangSelect(item.lang)"
								>
									<span>{{ item.name }}</span>
									<div class="lang-select-box-item-line"></div>
								</div>
							</div>
						</div>
					</ClientOnly>
					<a class="icon-box hidden-mobile" href="#" @click.prevent="handleSnsClick('x')">
						<img src="@/assets/imgs/x.png" alt="x" />
					</a>
					<a class="icon-box hidden-mobile" href="#" @click.prevent="handleSnsClick('discord')">
						<img src="@/assets/imgs/game.png" alt="game" />
					</a>
					<a class="icon-box hidden-mobile" href="#" @click.prevent="handleSnsClick('youtube')">
						<img src="@/assets/imgs/youtube.png" alt="youtube" />
					</a>
				</div>
				<div class="download-btn hidden-mobile" v-if="currentPage !== '#home'" @click="handleDownload">
					<img src="@/assets/imgs/icon-pc-white.png" alt="pc" />
					<span>{{ $t("header.download") }}</span>
				</div>
				<div class="mobile-menu hidden-pc" @click="isShowMobileMenu = !isShowMobileMenu">
					<img src="@/assets/imgs/icon-mobile-menu.png" alt="menu" />
				</div>
			</div>
		</div>
		<Transition>
			<MenuPage
				v-if="isShowMobileMenu"
				:pageRouterList="pageRouterList"
				@closeMenu="isShowMobileMenu = !isShowMobileMenu"
				@jumpPage="handlePageRouterClick"
				@handleSnsClick="handleSnsClick"
			/>
		</Transition>
	</header>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { pageRouterList } from "@/utils/config";
import { snsList } from "@/utils/config";
import MenuPage from "@/components/menuPage/index.vue";
import type { PageRouterItem } from "@/utils/config";
import logoWhite from "@/assets/imgs/hakko-logo-white.png";
import logoBlack from "@/assets/imgs/hakko-logo.png";
import { handleDownloadPC, handleDownloadApp } from "@/utils/download";
const router = useRouter();
const route = useRoute();
const { $eventBus } = useNuxtApp();

const { locale, defaultLocale, setLocale } = useI18n();

const props = defineProps({
	isErrorPage: {
		type: Boolean,
		default: false,
	},
});

const currentLang = ref(locale.value);
const currentPage = ref("#home");
const isShowMobileMenu = ref(false);
const isShowLangSelect = ref(false);
const langSelectList = ref([
	{
		id: 1,
		name: "日本語",
		lang: "ja" as const,
	},
	{
		id: 2,
		name: "English",
		lang: "en" as const,
	},
	{
		id: 3,
		name: "繁體中文",
		lang: "hant" as const,
	},
]);

const handlePageRouterClick = async (item: PageRouterItem) => {
	console.log(123)
	currentPage.value = item.path;
	if (
		(["/about", "/news"].includes(route.path) || route.path.includes("/news-detail")) &&
		!["/about", "/news"].includes(item.path)
	) {
		jumpHomeWithTab(item.path);
		return;
	}
	// 处理滚动到Swiper对应页面
	if (item.path.startsWith("#")) {
		if (route.path !== "/" && route.path !== `/${locale.value}`) {
			jumpHomeWithTab(item.path);
			return;
		}
		// 使用窗口事件触发滚动
		window.dispatchEvent(
			new CustomEvent("navigate-to-slide", {
				detail: { path: item.path },
			}),
		);
	} else {
		// 非锚点链接，使用路由导航
		router.push(locale.value === "ja" ? item.path : `/${locale.value}${item.path}`);
	}
};

const jumpHomeWithTab = (path: string) => {
	console.log(path, locale.value);
	router.push(locale.value === "ja" ? `/` : `/${locale.value}`);
	setTimeout(() => {
		window.dispatchEvent(
			new CustomEvent("navigate-to-slide", {
				detail: { path: path },
			}),
		);
	}, 300);
};

const handleDownload = () => {
	handleDownloadPC(locale.value);
};

const handleShowLangSelect = () => {
	isShowLangSelect.value = !isShowLangSelect.value;
};

const handleLangSelect = (lang: "ja" | "en" | "hant") => {
	currentLang.value = lang;
	setLocale(currentLang.value as "ja" | "en" | "hant");
	localStorage.setItem("lang", currentLang.value);
	isShowLangSelect.value = false;
};

const handleSnsClick = (type: string) => {
	const snsItem = snsList.value.find(item => item.nameKey === type);
	window.open(currentLang.value === "en" ? snsItem?.path_en : snsItem?.path, "_blank");
};

// 处理Swiper滑动时更新菜单状态
function handleUpdateActiveMenu(event: Event) {
	const customEvent = event as CustomEvent<{ path: string }>;
	const { path } = customEvent.detail;
	currentPage.value = path;
}

onMounted(() => {
	// 添加事件监听，当Swiper滑动时更新菜单状态
	window.addEventListener("update-active-menu", handleUpdateActiveMenu as EventListener);

	// 从localStorage中获取语言（只在客户端执行）
	if (process.client) {
		const lang = localStorage.getItem("lang");
		if (lang) {
			currentLang.value = lang === "en" ? "en" : lang === "hant" ? "hant" : "ja";
			setLocale(currentLang.value as "ja" | "en" | "hant");
		}
	}

	if (route.path.includes("/news")) {
		currentPage.value = "#news";
	}
	if (route.path.includes("/about")) {
		currentPage.value = "/about";
	}

	$eventBus.on("jumpTo", (pathInfo: PageRouterItem) => {
		handlePageRouterClick(pathInfo);
	});
});

onUnmounted(() => {
	// 移除事件监听器
	window.removeEventListener("update-active-menu", handleUpdateActiveMenu as EventListener);
	$eventBus.off("jumpTo");
});
</script>

<style lang="scss" scoped>
/* 移动设备断点 */
$mobile-breakpoint: 768px;

/* 基础样式与PC端样式 */
.header {
	height: 80px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	transition: all 1.5s ease-in-out;

	&.not-home {
		background: #fff;

		.page-router {
			.page-router-item {
				transition: all 1s ease-in-out;
				.page-router-item-ja {
					color: rgba(164, 164, 164, 1);
				}

				.page-router-item-en {
					color: rgba(164, 164, 164, 1);
				}
			}
		}

		.header-right {
			display: flex;
			width: fit-content;

			.header-right-icon-box {
				.lang-select-box-container {
					position: relative;
				}
				.icon-box {
					position: relative;
					img {
						filter: none;
					}
				}
			}
		}
	}

	.header-logo {
		margin-left: 140px;
		height: 80px;
		width: fit-content;
		display: flex;
		align-items: center;
		cursor: pointer;
		img {
			height: 36px;
		}
	}

	.page-router {
		display: flex;
		align-items: center;
		flex: 1;
		margin-right: 40px;
		justify-content: flex-end;

		.page-router-item {
			width: 120px;
			height: 80px;
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			cursor: pointer;

			.page-router-item-ja {
				font-family: Source Han Sans CN;
				font-weight: 400;
				font-size: 14px;
				line-height: 1em;
				color: rgba(169, 190, 233, 1);
			}

			.page-router-item-en {
				font-family: YuGothic;
				font-weight: 500;
				font-size: 10px;
				line-height: 12px;
				color: rgba(169, 190, 233, 1);
			}

			&.current {
				width: 140px;
				height: 80px;
				background: url("@/assets/imgs/route-bg.webp") no-repeat;
				background-size: 100% 100%;

				.page-router-item-ja {
					font-weight: 700;
					font-size: 18px;
					line-height: 1em;
					color: #fff;
					text-shadow: 0px 0px 2px #e64477e5;
				}
			}
		}
	}

	.header-right {
		display: flex;
		align-items: center;
		margin-left: auto;
		margin-right: 138px;

		.header-right-icon-box {
			display: flex;
			align-items: center;
			gap: 14px;
			margin-right: 20px;
			.lang-select-box-container {
				position: relative;
				.lang-select-box {
					position: absolute;
					top: 100%;
					left: 50%;
					transform: translateX(-50%);
					margin-top: 14px;
					width: 110px;
					height: calc(24px * 3);
					z-index: 100;
					background: #fff;
					border-radius: 10px;
					box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
					overflow: hidden;
					.lang-select-box-item {
						width: 100%;
						height: 24px;
						display: flex;
						align-items: center;
						justify-content: center;
						cursor: pointer;
						position: relative;
						&.active {
							background: #ff8ab1;
							span {
								color: #fff;
							}
							.lang-select-box-item-line {
								height: 0;
							}
						}
						span {
							color: rgba(187, 187, 187, 1);
							font-size: 12px;
						}
						.lang-select-box-item-line {
							position: absolute;
							bottom: 0;
							left: 50%;
							transform: translateX(-50%);
							width: 97px;
							height: 1px;
							background: rgba(236, 236, 236, 1);
						}
					}
				}
			}

			.icon-box {
				width: 24px;
				height: 24px;
				display: flex;

				img {
					width: 100%;
					filter: brightness(0) invert(1);
				}
			}
		}

		.download-btn {
			height: 40px;
			padding: 11px 25px;
			border-radius: 10px;
			display: flex;
			align-items: center;
			justify-content: center;
			background: linear-gradient(273.99deg, #7fb9ff 5.63%, #ffa0c0 50.52%);
			cursor: pointer;
			img {
				width: 40px;
				height: 40px;
				margin-right: 6px;
			}

			span {
				font-family: Source Han Sans CN;
				font-weight: 700;
				font-size: 16px;
				color: #fff;
				white-space: nowrap;
			}
		}
	}
}

/* pc端样式 */
@media (min-width: $mobile-breakpoint) {
	.header-right {
		min-width: 250px;
	}
}

/* 移动端样式 */
@media (max-width: $mobile-breakpoint) {
	.header {
		height: 40px;

		&.not-home {
			.mobile-menu {
				margin-left: 10px;
				filter: invert(1);
			}
		}

		.header-logo {
			height: 40px;
			margin-left: 14px;

			img {
				height: 14px;
			}
		}

		.page-router {
			display: none; /* 移动端可以考虑使用汉堡菜单替代 */
		}

		.header-right {
			margin-right: 20px;

			.header-right-icon-box {
				gap: 10px;
				margin: 0 12px;

				.icon-box {
					width: 20px;
					height: 20px;
				}
			}

			.download-btn {
				height: 26px;
				border-radius: 6px;
				padding: 0 10px;
				img {
					width: 18px;
					height: 18px;
				}

				span {
					font-size: 12px;
				}
			}
			.mobile-menu {
				width: 20px;
				height: 20px;
				display: flex;

				img {
					width: 100%;
					height: 100%;
				}
			}
		}
	}
}
</style>
